import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Text,
  Button,
  Group,
  Title,
  Badge,
  Center,
  Loader,
  Divider,
  Stack,
  ThemeIcon,
  Modal,
  Stepper,
  Box,
  TextInput,
  PasswordInput,
  Alert,
  SimpleGrid,
  CopyButton,
  ActionIcon,
  Tooltip,
  Image,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../../routes/routesPaths";
import {
  IconCreditCard,
  IconCheck,
  IconCopy,
  IconExternalLink,
  IconAlertCircle,
  IconRefresh,
  IconKey,
  IconShieldCheck,
  IconInfoCircle,
  IconLock,
  IconTrash,
} from "@tabler/icons-react";
import {
  doc,
  collection,
  query,
  where,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, functions } from "../../../../firebase/FirebaseCredential";
import { useAppSelector } from "../../../../hooks/hooks";
import { RootState } from "../../../../store/store";
import { httpsCallable } from "firebase/functions";
import { toast } from "react-toastify";
import BenchSettingsBreadcrumbs from "../../../UIComponent/Components/BenchSettingsBreadcrumbs";
import BenchCard from "../../../UIComponent/Components/BenchCard";
import razorpayLogo from "../../../../assets/UiSvg/razorpay.png";

const RAZORPAY_SIGNUP_URL = "https://rzp.io/rzp/ENJBmCb";

interface RazorpayConfigData {
  merchant_id?: string;
  key_id?: string;
  key_secret?: string;
  mode?: "live" | "test";
  status?: string;
  isSetup?: boolean;
  account_details?: {
    verified?: boolean;
    mode?: string;
    currency?: string;
    merchant_id?: string;
    business_name?: string;
    email?: string;
    phone?: string;
    supported_methods?: string[];
    payments_count?: number;
    last_verified_at?: string;
  };
  created_at?: any;
  updated_at?: any;
}

export default function RazorpayConnect() {
  const navigate = useNavigate();
  const companyId = useAppSelector(
    (state: RootState) => state.session?.company_id,
  );

  const [loading, setLoading] = useState(true);
  const [docId, setDocId] = useState<string | null>(null);
  const [config, setConfig] = useState<RazorpayConfigData | null>(null);

  // Guide Modal (Steps)
  const [guideOpened, { open: openGuide, close: closeGuide }] =
    useDisclosure(false);

  // Connect / Edit Credentials Modal
  const [
    connectModalOpened,
    { open: openConnectModal, close: closeConnectModal },
  ] = useDisclosure(false);

  // Form states for Modal
  const [merchantId, setMerchantId] = useState("");
  const [keyId, setKeyId] = useState("");
  const [keySecret, setKeySecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [
    disconnectModalOpened,
    { open: openDisconnectModal, close: closeDisconnectModal },
  ] = useDisclosure(false);

  // Listen to Firestore settings by querying for type == "razorpay"
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "companyprofile", companyId, "paymentcollection"),
      where("type", "==", "razorpay"),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!snapshot.empty) {
          const docSnap = snapshot.docs[0];
          const data = docSnap.data() as RazorpayConfigData;
          setDocId(docSnap.id);
          setConfig(data);
          // Pre-populate modal fields if existing
          setMerchantId(data.merchant_id || "");
          setKeyId(data.key_id || "");
          setKeySecret(data.key_secret || "");
        } else {
          setDocId(null);
          setConfig(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching Razorpay configuration:", error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [companyId]);

  const isConfigured = Boolean(
    config &&
    config.key_id &&
    config.key_secret &&
    (config.status === "Active" || config.isSetup === true),
  );

  // Open Connect Modal with fresh inputs
  const handleOpenConnect = () => {
    if (config) {
      setMerchantId(config.merchant_id || "");
      setKeyId(config.key_id || "");
      setKeySecret(config.key_secret || "");
    }
    openConnectModal();
  };

  // Detect mode based on key ID prefix
  const detectedMode = keyId.trim().startsWith("rzp_live")
    ? "live"
    : keyId.trim().startsWith("rzp_test")
      ? "test"
      : null;

  // Save Credentials & Verify with Razorpay
  const handleSaveAndVerify = async () => {
    if (!companyId) {
      toast.error("Company ID not found.");
      return;
    }

    const trimmedMerchantId = merchantId.trim();
    const trimmedKeyId = keyId.trim();
    const trimmedKeySecret = keySecret.trim();

    if (!trimmedMerchantId) {
      toast.error("Please enter your Razorpay Merchant ID.");
      return;
    }

    if (!trimmedKeyId) {
      toast.error("Please enter your Razorpay Key ID.");
      return;
    }

    if (!trimmedKeySecret) {
      toast.error("Please enter your Razorpay Key Secret.");
      return;
    }

    if (
      !trimmedKeyId.startsWith("rzp_live_") &&
      !trimmedKeyId.startsWith("rzp_test_")
    ) {
      toast.warning(
        "Key ID usually starts with 'rzp_live_' or 'rzp_test_'. Please check your credentials.",
      );
    }

    setSaving(true);
    const mode = trimmedKeyId.startsWith("rzp_live") ? "live" : "test";

    try {
      // 1. Attempt verification and account detail retrieval via Cloud Function
      let verifiedDetails: any = {
        verified: true,
        mode,
        currency: "INR",
        merchant_id: trimmedMerchantId,
        key_id: trimmedKeyId,
        supported_methods: [
          "UPI",
          "Cards",
          "NetBanking",
          "Wallets",
          "Auto-pay Mandates",
        ],
        last_verified_at: new Date().toISOString(),
      };

      try {
        const verifyFn = httpsCallable(functions, "getRazorpayAccountDetails");
        const res: any = await verifyFn({
          companyId,
          key_id: trimmedKeyId,
          key_secret: trimmedKeySecret,
          merchant_id: trimmedMerchantId,
          saveToDb: false, // We commit from client or fallback
        });

        if (res?.data?.success && res?.data?.data) {
          verifiedDetails = {
            ...verifiedDetails,
            ...res.data.data,
            last_verified_at: new Date().toISOString(),
          };
        } else if (res?.data && res.data.verified === false) {
          toast.error(
            res.data.message || "Failed to verify credentials with Razorpay.",
          );
          setSaving(false);
          return;
        }
      } catch (fnErr: any) {
        console.warn(
          "Cloud function getRazorpayAccountDetails skipped/failed, using client persistence:",
          fnErr?.message,
        );
      }

      // 2. Persist to Firestore in companyprofile/{companyId}/paymentcollection with auto-generated ID
      const paymentCollectionRef = collection(
        db,
        "companyprofile",
        companyId,
        "paymentcollection",
      );

      // If existing document found for type="razorpay", update it; otherwise auto-generate a new doc ID
      const targetDocRef = docId
        ? doc(paymentCollectionRef, docId)
        : doc(paymentCollectionRef);

      await setDoc(
        targetDocRef,
        {
          type: "razorpay",
          company_id: companyId,
          merchant_id: trimmedMerchantId,
          key_id: trimmedKeyId,
          key_secret: trimmedKeySecret,
          mode,
          status: "Active",
          isSetup: true,
          isDefault: false,
          account_details: verifiedDetails,
          updated_at: new Date(),
          created_at: config?.created_at || serverTimestamp(),
        },
        { merge: true },
      );

      setDocId(targetDocRef.id);

      toast.success("Razorpay payment gateway connected successfully!");
      closeConnectModal();
    } catch (err: any) {
      console.error("Error saving Razorpay settings:", err);
      toast.error(err.message || "Failed to save Razorpay settings.");
    } finally {
      setSaving(false);
    }
  };

  // Re-verify existing credentials and fetch live account details
  const handleRefreshDetails = async () => {
    if (!companyId || !config?.key_id || !config?.key_secret) {
      toast.error("Credentials missing to verify.");
      return;
    }

    setVerifying(true);
    try {
      const verifyFn = httpsCallable(functions, "getRazorpayAccountDetails");
      const res: any = await verifyFn({
        companyId,
        key_id: config.key_id,
        key_secret: config.key_secret,
        merchant_id: config.merchant_id || "",
        saveToDb: true,
      });

      if (res?.data?.success && res?.data?.data) {
        toast.success("Razorpay account details refreshed and verified!");
      } else if (res?.data?.verified === false) {
        toast.error(
          res.data.message || "Credentials could not be verified by Razorpay.",
        );
      } else {
        toast.info("Account details verified.");
      }
    } catch (err: any) {
      console.error("Error re-verifying Razorpay details:", err);
      toast.warning(
        "Unable to reach verification service. Current credentials remain active.",
      );
    } finally {
      setVerifying(false);
    }
  };

  // Disconnect Razorpay Gateway
  const handleDisconnect = async () => {
    if (!companyId || !docId) return;
    try {
      const docRef = doc(
        db,
        "companyprofile",
        companyId,
        "paymentcollection",
        docId,
      );
      await deleteDoc(docRef);
      setDocId(null);
      setConfig(null);
      toast.success("Razorpay account disconnected.");
      closeDisconnectModal();
    } catch (err: any) {
      console.error("Error disconnecting Razorpay:", err);
      toast.error("Failed to disconnect Razorpay.");
    }
  };

  const breadcrumbLinks = [
    { label: "Payment Collection", path: ROUTES.PAYMENT },
    { label: "Razorpay Connect" },
  ];

  if (loading) {
    return (
      <Center style={{ height: "80vh" }}>
        <Loader size="xl" color="brand" />
      </Center>
    );
  }

  return (
    <Container size="lg" p="lg">
      {/* Top Breadcrumb Nav Bar */}
      <Box
        sx={(theme) => ({
          position: "sticky",
          top: 110,
          zIndex: 100,
          backgroundColor: "#FAFAFA",
          paddingTop: theme.spacing.sm,
          marginTop: `-${theme.spacing.sm}`,
        })}
      >
        <BenchSettingsBreadcrumbs links={breadcrumbLinks} />
        <Divider my="sm" />
      </Box>

      {/* Main Container Card */}
      <Paper
        shadow="xs"
        radius="md"
        p={40}
        mb={20}
        withBorder
        sx={{ backgroundColor: "#fff" }}
      >
        <Stack spacing={30}>
          {/* Header Row: Logo, Title, Badge & Primary Action Button */}
          <Group position="apart" align="flex-start">
            <Stack spacing={4}>
              <Group spacing={14}>
                <ThemeIcon size={44} radius="md" color="brand" variant="filled">
                  <Image
                    src={razorpayLogo}
                    alt="Razorpay"
                    fit="contain"
                    width={28}
                    height={28}
                  />
                </ThemeIcon>
                <div>
                  <Group spacing="xs">
                    <Title
                      order={2}
                      fw={800}
                      style={{ letterSpacing: "-0.5px" }}
                    >
                      Razorpay Payment Gateway
                    </Title>
                    {isConfigured ? (
                      <Badge
                        color="red"
                        variant="filled"
                        size="sm"
                        leftSection={<IconCheck size={12} />}
                      >
                        Active
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" size="sm">
                        Not Connected
                      </Badge>
                    )}
                    {isConfigured && config?.mode && (
                      <Badge
                        color={config.mode === "live" ? "red" : "orange"}
                        variant="outline"
                        size="sm"
                      >
                        {config.mode === "live" ? "Live Mode" : "Test Mode"}
                      </Badge>
                    )}
                  </Group>
                  <Text size="sm" color="dimmed">
                    Online Payments, Automated Reconciliation &amp; Auto-Pay
                    Mandates
                  </Text>
                </div>
              </Group>

              <Text
                size="md"
                color="dark"
                mt="md"
                style={{ maxWidth: "660px" }}
              >
                Connect your Razorpay account to collect membership payments
                seamlessly via Credit/Debit Cards, UPI, Netbanking, QR codes,
                and automated subscriptions.
              </Text>
            </Stack>

            <Group spacing="xs">
              {isConfigured && (
                <Button
                  size="md"
                  radius="md"
                  variant="default"
                  loading={verifying}
                  leftIcon={<IconRefresh size={16} />}
                  onClick={handleRefreshDetails}
                >
                  Verify &amp; Sync
                </Button>
              )}

              <Button
                size="md"
                radius="md"
                variant={isConfigured ? "outline" : "filled"}
                onClick={handleOpenConnect}
                style={{
                  backgroundColor: isConfigured ? "transparent" : "#ee374d",
                  color: isConfigured ? "#ee374d" : "#fff",
                  borderColor: "#ee374d",
                  paddingLeft: 28,
                  paddingRight: 28,
                }}
              >
                {isConfigured ? "Edit Configuration" : "Connect"}
              </Button>
            </Group>
          </Group>

          {/* Configured Details Section (when connected) */}
          {isConfigured && config && (
            <div>
              <Title order={4} fw={700} mb="md">
                Connected Razorpay Account Overview
              </Title>
              <SimpleGrid
                cols={3}
                breakpoints={[{ maxWidth: "sm", cols: 1 }]}
                spacing="md"
              >
                {/* Merchant ID Card */}
                <BenchCard p="md">
                  <Group position="apart" align="flex-start">
                    <Stack spacing={2}>
                      <Text
                        size="xs"
                        color="dimmed"
                        transform="uppercase"
                        weight={700}
                      >
                        Merchant ID
                      </Text>
                      <Text weight={700} size="md" color="dark">
                        {config.merchant_id || "Not Set"}
                      </Text>
                    </Stack>
                    {config.merchant_id && (
                      <CopyButton value={config.merchant_id} timeout={2000}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied" : "Copy Merchant ID"}
                            withArrow
                          >
                            <ActionIcon
                              color={copied ? "teal" : "gray"}
                              onClick={copy}
                              size="sm"
                            >
                              {copied ? (
                                <IconCheck size={16} />
                              ) : (
                                <IconCopy size={16} />
                              )}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    )}
                  </Group>
                </BenchCard>

                {/* API Key ID Card */}
                <BenchCard p="md">
                  <Group position="apart" align="flex-start">
                    <Stack spacing={2}>
                      <Text
                        size="xs"
                        color="dimmed"
                        transform="uppercase"
                        weight={700}
                      >
                        Key ID ({config.mode === "live" ? "Live" : "Test"})
                      </Text>
                      <Text weight={700} size="md" color="dark">
                        {config.key_id || "Not Set"}
                      </Text>
                    </Stack>
                    {config.key_id && (
                      <CopyButton value={config.key_id} timeout={2000}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied" : "Copy Key ID"}
                            withArrow
                          >
                            <ActionIcon
                              color={copied ? "teal" : "gray"}
                              onClick={copy}
                              size="sm"
                            >
                              {copied ? (
                                <IconCheck size={16} />
                              ) : (
                                <IconCopy size={16} />
                              )}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    )}
                  </Group>
                </BenchCard>

                {/* API Key Secret Card */}
                <BenchCard p="md">
                  <Group position="apart" align="flex-start">
                    <Stack spacing={2}>
                      <Text
                        size="xs"
                        color="dimmed"
                        transform="uppercase"
                        weight={700}
                      >
                        Key Secret
                      </Text>
                      <Text weight={700} size="md" color="dark">
                        {showSecret ? config.key_secret : "••••••••••••••••"}
                      </Text>
                    </Stack>
                    <Group spacing={4}>
                      <ActionIcon
                        size="sm"
                        color="gray"
                        onClick={() => setShowSecret((prev) => !prev)}
                      >
                        <IconKey size={16} />
                      </ActionIcon>
                      {config.key_secret && (
                        <CopyButton value={config.key_secret} timeout={2000}>
                          {({ copied, copy }) => (
                            <Tooltip
                              label={copied ? "Copied" : "Copy Secret"}
                              withArrow
                            >
                              <ActionIcon
                                color={copied ? "teal" : "gray"}
                                onClick={copy}
                                size="sm"
                              >
                                {copied ? (
                                  <IconCheck size={16} />
                                ) : (
                                  <IconCopy size={16} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      )}
                    </Group>
                  </Group>
                </BenchCard>
              </SimpleGrid>

              {/* Account Capabilities & Verification Status */}
              <BenchCard p="md" mt="md">
                <SimpleGrid
                  cols={3}
                  breakpoints={[{ maxWidth: "sm", cols: 1 }]}
                  spacing="md"
                >
                  <div>
                    <Text
                      size="xs"
                      color="dimmed"
                      fw={600}
                      transform="uppercase"
                    >
                      Supported Payment Modes
                    </Text>
                    <Group spacing={6} mt={6}>
                      {["UPI", "Cards", "NetBanking", "Auto-pay"].map(
                        (method) => (
                          <Badge
                            key={method}
                            size="sm"
                            variant="dot"
                            color="brand"
                          >
                            {method}
                          </Badge>
                        ),
                      )}
                    </Group>
                  </div>

                  <div>
                    <Text
                      size="xs"
                      color="dimmed"
                      fw={600}
                      transform="uppercase"
                    >
                      Settlement Currency
                    </Text>
                    <Text size="sm" fw={700} mt={6}>
                      INR (₹) Indian Rupee
                    </Text>
                  </div>

                  <div>
                    <Text
                      size="xs"
                      color="dimmed"
                      fw={600}
                      transform="uppercase"
                    >
                      Security &amp; Health Check
                    </Text>
                    <Group spacing={6} mt={6}>
                      <ThemeIcon size={20} radius="xl" color="brand">
                        <IconShieldCheck size={14} />
                      </ThemeIcon>
                      <Text size="xs" color="dimmed">
                        {config.account_details?.last_verified_at
                          ? `Verified ${new Date(config.account_details.last_verified_at).toLocaleDateString()}`
                          : "Credentials Active"}
                      </Text>
                    </Group>
                  </div>
                </SimpleGrid>

                <Divider my="md" />

                <Group position="apart">
                  <Text size="xs" color="dimmed">
                    Payments collected through Razorpay will be automatically
                    tracked in Bench and reconciled with member invoices.
                  </Text>
                  <Button
                    variant="subtle"
                    color="red"
                    size="xs"
                    leftIcon={<IconTrash size={14} />}
                    onClick={openDisconnectModal}
                  >
                    Disconnect Razorpay
                  </Button>
                </Group>
              </BenchCard>
            </div>
          )}

          {/* Show Onboarding / Benefits / Requirements only when not yet configured */}
          {!isConfigured && (
            <>
              {/* Benefits Section */}
              <div>
                <Title order={4} fw={700} mb="md">
                  Benefits of Razorpay Integration
                </Title>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "20px",
                    listStyleType: "disc",
                  }}
                >
                  <li style={{ marginBottom: "8px" }}>
                    <Text size="sm">
                      <b>Comprehensive Payment Options:</b> Accept UPI (Google
                      Pay, PhonePe, Paytm), Credit/Debit cards, Net Banking, and
                      popular digital wallets.
                    </Text>
                  </li>
                  <li style={{ marginBottom: "8px" }}>
                    <Text size="sm">
                      <b>Automated Invoice Reconciliation:</b> Every online
                      transaction updates member subscriptions and marks
                      invoices as paid automatically in real time.
                    </Text>
                  </li>
                  <li style={{ marginBottom: "8px" }}>
                    <Text size="sm">
                      <b>Instant Payment Links:</b> Send payment links via SMS,
                      WhatsApp, and Email with built-in customer reminders.
                    </Text>
                  </li>
                  <li>
                    <Text size="sm">
                      <b>Auto-pay &amp; Recurring Subscriptions:</b> Collect
                      recurring gym membership fees with automated e-mandates.
                    </Text>
                  </li>
                </ul>
              </div>

              {/* Requirements & Sign-up Section */}
              <div>
                <Title order={4} fw={700} mb="md">
                  Before you can connect Razorpay with Bench, you must
                </Title>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "20px",
                    listStyleType: "disc",
                  }}
                >
                  <li style={{ marginBottom: "10px" }}>
                    <Text size="sm">
                      Create a Razorpay merchant account if you don't already
                      have one.{" "}
                      <Text
                        component="a"
                        onClick={() =>
                          window.open(RAZORPAY_SIGNUP_URL, "_blank")
                        }
                        color="red"
                        sx={{ cursor: "pointer", fontWeight: 700 }}
                      >
                        Sign up with Razorpay{" "}
                        <IconExternalLink
                          size={13}
                          style={{ verticalAlign: "middle" }}
                        />
                      </Text>
                    </Text>
                  </li>
                  <li style={{ marginBottom: "8px" }}>
                    <Text size="sm">
                      Log in to your <b>Razorpay Dashboard</b> &rarr;{" "}
                      <b>Account &amp; Settings</b> &rarr; <b>API Keys</b> and
                      generate your <b>Key ID</b> and <b>Key Secret</b>.
                    </Text>
                  </li>
                  <li style={{ marginBottom: "8px" }}>
                    <Text size="sm">
                      Find your <b>Merchant ID</b> located in your Razorpay
                      Dashboard under Profile Settings (top-right corner).
                    </Text>
                  </li>
                  <li>
                    <Text size="sm">
                      Have active KYC verification on Razorpay for live
                      settlements.{" "}
                      <Text
                        component="a"
                        onClick={openGuide}
                        color="red"
                        sx={{ cursor: "pointer", fontWeight: 600 }}
                      >
                        Read setup guide.
                      </Text>
                    </Text>
                  </li>
                </ul>
              </div>

              {/* Sign up Callout Banner */}
              <Alert
                icon={<IconInfoCircle size={20} />}
                title="New to Razorpay?"
                color="red"
                radius="md"
                sx={{ backgroundColor: "#FFF5F6", border: "1px solid #FFE0E4" }}
              >
                <Group position="apart" align="center">
                  <Text size="sm">
                    Sign up through Bench to quickly get started with special
                    pricing, instant onboarding, and dedicated gateway support.
                  </Text>
                  <Button
                    size="sm"
                    color="brand"
                    variant="filled"
                    style={{ backgroundColor: "#ee374d" }}
                    rightIcon={<IconExternalLink size={14} />}
                    onClick={() => window.open(RAZORPAY_SIGNUP_URL, "_blank")}
                  >
                    Sign Up Now
                  </Button>
                </Group>
              </Alert>
            </>
          )}
        </Stack>
      </Paper>

      {/* Connect / Edit Credentials Modal */}
      <Modal
        opened={connectModalOpened}
        onClose={closeConnectModal}
        title={
          <Group spacing="xs">
            <ThemeIcon size={30} radius="md" color="brand" variant="light">
              <IconCreditCard size={18} />
            </ThemeIcon>
            <Title order={3} fw={800}>
              {isConfigured
                ? "Edit Razorpay Configuration"
                : "Connect Razorpay Gateway"}
            </Title>
          </Group>
        }
        size="lg"
        padding="xl"
        radius="md"
        overlayProps={{
          blur: 3,
          opacity: 0.55,
        }}
      >
        <Stack spacing="lg">
          <Text size="sm" color="dimmed">
            Enter your Razorpay API credentials from your Razorpay Dashboard.
            Once saved, Bench will securely verify the keys and enable automated
            payment processing.
          </Text>

          {/* Sign Up Banner inside modal */}
          <Paper
            p="sm"
            radius="md"
            sx={{ backgroundColor: "#FFF5F6", border: "1px solid #FFE0E4" }}
          >
            <Group position="apart" align="center">
              <div>
                <Text size="xs" fw={700} color="#ee374d">
                  DON'T HAVE A RAZORPAY ACCOUNT?
                </Text>
                <Text size="xs" color="dimmed">
                  Create a new account now and complete your quick KYC.
                </Text>
              </div>
              <Button
                size="xs"
                variant="outline"
                color="brand"
                style={{ borderColor: "#ee374d", color: "#ee374d" }}
                rightIcon={<IconExternalLink size={12} />}
                onClick={() => window.open(RAZORPAY_SIGNUP_URL, "_blank")}
              >
                Sign up
              </Button>
            </Group>
          </Paper>

          {/* Merchant ID Input */}
          <TextInput
            label="Merchant ID"
            placeholder="e.g. acc_XXXXXX or merchant profile ID"
            description="Found in your Razorpay Dashboard under your profile / account settings."
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
            required
            withAsterisk
          />

          {/* Key ID Input */}
          <TextInput
            label="Key ID"
            placeholder="rzp_live_xxxxxxxx or rzp_test_xxxxxxxx"
            description="Your public API key from Razorpay Dashboard > Settings > API Keys."
            value={keyId}
            onChange={(e) => setKeyId(e.target.value)}
            required
            withAsterisk
            rightSection={
              detectedMode && (
                <Badge
                  size="xs"
                  color={detectedMode === "live" ? "red" : "orange"}
                  variant="filled"
                >
                  {detectedMode === "live" ? "Live" : "Test"}
                </Badge>
              )
            }
          />

          {/* Key Secret Input */}
          <PasswordInput
            label="Key Secret"
            placeholder="Enter your API Key Secret"
            description="Keep this safe. Generated alongside your Key ID in the Razorpay Dashboard."
            value={keySecret}
            onChange={(e) => setKeySecret(e.target.value)}
            required
            withAsterisk
            icon={<IconLock size={16} />}
          />

          <Divider my="xs" />

          <Group position="right">
            <Button
              variant="subtle"
              color="gray"
              onClick={closeConnectModal}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              color="brand"
              onClick={handleSaveAndVerify}
              loading={saving}
              style={{ backgroundColor: "#ee374d" }}
            >
              {isConfigured ? "Update & Verify" : "Connect & Verify"}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Setup Guide Stepper Modal (like whatsapp.tsx) */}
      <Modal
        opened={guideOpened}
        onClose={closeGuide}
        title={
          <Title order={3} fw={800}>
            Configuring Razorpay with Bench
          </Title>
        }
        size="lg"
        padding="xl"
        radius="md"
        overlayProps={{
          blur: 3,
          opacity: 0.55,
        }}
      >
        <Stack spacing="lg">
          <Text size="sm" color="dimmed">
            Follow these simple steps to integrate your Razorpay account with
            Bench and start collecting online payments instantly.
          </Text>

          <Stepper
            active={-1}
            orientation="vertical"
            size="sm"
            breakpoint="sm"
            color="brand"
          >
            <Stepper.Step
              label="Step 1: Create Razorpay Account"
              description={
                <Box mt={5}>
                  <Text size="xs">
                    <Text
                      component="a"
                      onClick={() => window.open(RAZORPAY_SIGNUP_URL, "_blank")}
                      color="red"
                      sx={{ cursor: "pointer", fontWeight: 700 }}
                    >
                      Sign up here
                    </Text>{" "}
                    for a new Razorpay merchant account if you don't have one
                    already.
                  </Text>
                </Box>
              }
            />
            <Stepper.Step
              label="Step 2: Generate API Key &amp; Secret"
              description={
                <Box mt={5}>
                  <Text size="xs">
                    Log in to your Razorpay Dashboard, navigate to{" "}
                    <b>Account &amp; Settings</b> &rarr; <b>API Keys</b>, and
                    click <b>Generate Key</b> to get your <b>Key ID</b> and{" "}
                    <b>Key Secret</b>.
                  </Text>
                </Box>
              }
            />
            <Stepper.Step
              label="Step 3: Retrieve Merchant ID"
              description={
                <Box mt={5}>
                  <Text size="xs">
                    Click on your profile avatar in the top-right corner of the
                    Razorpay Dashboard to find your unique <b>Merchant ID</b>.
                  </Text>
                </Box>
              }
            />
            <Stepper.Step
              label="Step 4: Save &amp; Connect"
              description={
                <Box mt={5}>
                  <Text size="xs">
                    Click the <b>Connect</b> button on this page, enter your
                    Merchant ID, Key ID, and Key Secret, and click{" "}
                    <b>Connect &amp; Verify</b>. Bench will verify your gateway
                    and activate automated payment processing!
                  </Text>
                </Box>
              }
            />
          </Stepper>

          <Divider my="sm" />

          <Group position="right">
            <Button variant="light" color="gray" onClick={closeGuide}>
              Close
            </Button>
            <Button
              color="brand"
              style={{ backgroundColor: "#ee374d" }}
              onClick={() => {
                closeGuide();
                handleOpenConnect();
              }}
            >
              Go to Connect
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Disconnect Confirmation Modal */}
      <Modal
        opened={disconnectModalOpened}
        onClose={closeDisconnectModal}
        title={
          <Group spacing="xs">
            <IconAlertCircle color="red" size={20} />
            <Text fw={700}>Disconnect Razorpay Gateway?</Text>
          </Group>
        }
        size="md"
        centered
      >
        <Stack spacing="md">
          <Text size="sm">
            Are you sure you want to disconnect Razorpay? Members will not be
            able to pay online via Razorpay until you reconnect your
            credentials.
          </Text>
          <Group position="right">
            <Button variant="light" color="gray" onClick={closeDisconnectModal}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDisconnect}>
              Yes, Disconnect
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
